<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
	label: string
	/** RMS, 0-1. Speech sits well below 1, so the bar is scaled to a useful range. */
	level: number
	hint?: string
	muted?: boolean
}>()

/** Speech RMS rarely exceeds ~0.3, so the bar would barely move on a linear 0-1 scale. */
const width = computed(() => Math.min(100, Math.round((props.level / 0.3) * 100)))
</script>

<template>
	<div class="meter" :class="{ muted }">
		<div class="row">
			<span class="label">{{ label }}</span>
			<span v-if="hint" class="hint">{{ hint }}</span>
		</div>
		<div class="track" role="meter" :aria-valuenow="width" aria-valuemin="0" aria-valuemax="100" :aria-label="label">
			<div class="fill" :style="{ width: `${width}%` }" />
		</div>
	</div>
</template>

<style scoped>
.meter {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.row {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	align-items: baseline;
}

.label {
	font-size: 0.875rem;
	font-weight: 500;
}

.hint {
	font-size: 0.8125rem;
	color: var(--tcx-muted);
	font-variant-numeric: tabular-nums;
}

.track {
	height: 8px;
	border-radius: 999px;
	background: rgba(63, 63, 63, 0.09);
	overflow: hidden;
}

.fill {
	height: 100%;
	border-radius: 999px;
	background: var(--tcx-teal);
	transition: width 0.1s linear;
}

.muted .fill {
	background: #c9cfcf;
}
</style>
